import type { PropsWithChildren } from "react";

import { Accordion, CanAccess, Drawer } from "@eisox/design-system";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import {
  canAccessCalibrationSettings,
  canAccessFrostFreeSettings,
  canAccessFunctionsSettings,
  canAccessGeneralSettings,
  canAccessTemperatureSettings,
} from "../helpers";
import { CalibrationSettings } from "./calibration-settings";
import { FunctionsSettings } from "./functions-settings";
import { GeneralSettings } from "./general-settings";
import { HolidaysSettings } from "./holidays-settings";
import { TemperatureSettings } from "./temperature-settings";

const NAME = "Settings";

interface SettingsProps extends PropsWithChildren, Drawer.DrawerProps {
  value?: "general" | "temperatures" | "frostFree" | "functions" | "calibration";
  asChild?: boolean;
}

export const Settings: React.FC<SettingsProps> = ({
  children,
  value,
  asChild,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange: onOpenChangeProp,
  defaultOpen = false,
  ...props
}) => {
  const { permissions } = useBuildingContext(NAME);

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChangeProp,
    defaultProp: defaultOpen,
  });

  const getDefaultValue = () => {
    const settingsConfig = [
      { key: "general", check: canAccessGeneralSettings },
      { key: "temperatures", check: canAccessTemperatureSettings },
      { key: "frostFree", check: canAccessFrostFreeSettings },
      { key: "functions", check: canAccessFunctionsSettings },
      { key: "calibration", check: canAccessCalibrationSettings },
    ];

    const currentSetting = settingsConfig.find(s => s.key === value);
    if (currentSetting?.check(permissions)) return value;

    const fallbackSetting = settingsConfig.find(s => s.check(permissions));
    return fallbackSetting ? fallbackSetting.key : undefined;
  };

  return (
    <Drawer.Root {...props} open={open} onOpenChange={setOpen}>
      {children && <Drawer.Trigger asChild={asChild}>{children}</Drawer.Trigger>}
      <Drawer.Content>
        <Drawer.Header>{m.settings()}</Drawer.Header>
        <div className="md:p-5 p-2.5 overflow-y-auto">
          <Accordion.Root type="single" defaultValue={getDefaultValue()} collapsible>
            <CanAccess isAuthorized={canAccessGeneralSettings(permissions)}>
              <Accordion.Item value="general">
                <Accordion.Trigger>{m.general()}</Accordion.Trigger>
                <Accordion.Content>
                  <GeneralSettings />
                </Accordion.Content>
              </Accordion.Item>
            </CanAccess>
            <CanAccess isAuthorized={canAccessTemperatureSettings(permissions)}>
              <Accordion.Item value="temperatures">
                <Accordion.Trigger>{m.setpoint()}</Accordion.Trigger>
                <Accordion.Content>
                  <TemperatureSettings />
                </Accordion.Content>
              </Accordion.Item>
            </CanAccess>
            <CanAccess isAuthorized={canAccessFrostFreeSettings(permissions)}>
              <Accordion.Item value="frostFree">
                <Accordion.Trigger>{m.holidays()}</Accordion.Trigger>
                <Accordion.Content>
                  <HolidaysSettings />
                </Accordion.Content>
              </Accordion.Item>
            </CanAccess>
            <CanAccess isAuthorized={canAccessFunctionsSettings(permissions)}>
              <Accordion.Item value="functions">
                <Accordion.Trigger>{m.functions()}</Accordion.Trigger>
                <Accordion.Content>
                  <FunctionsSettings />
                </Accordion.Content>
              </Accordion.Item>
            </CanAccess>
            <CanAccess isAuthorized={canAccessCalibrationSettings(permissions)}>
              <Accordion.Item value="calibration">
                <Accordion.Trigger>{m.calibration()}</Accordion.Trigger>
                <Accordion.Content>
                  <CalibrationSettings />
                </Accordion.Content>
              </Accordion.Item>
            </CanAccess>
          </Accordion.Root>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
};

Settings.displayName = NAME;
